import { Search, Sun, Moon, Bell, LogOut, Sparkles, Building2, Check, ChevronDown } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useTheme } from "@/lib/theme";
import { useAuth, useLogout, useSwitchTenant } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

function initials(nameOrEmail: string): string {
  const parts = nameOrEmail.replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
  return letters || "?";
}

export default function AppTopbar({ onOpenChat }: { onOpenChat?: () => void }) {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const logout = useLogout();
  const switchTenant = useSwitchTenant();
  const nav = useNavigate();

  async function onLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      nav("/login", { replace: true });
    }
  }

  // Tenant selector visibility rule: only render when the user has more
  // than one tenant to choose from. For Cambridge admin this stays
  // hidden; for Stefan-the-platform-admin it shows every active tenant.
  const canSwitchTenant = (user?.visible_tenants?.length ?? 0) > 1;
  const activeTenant =
    user?.visible_tenants?.find((t) => t.id === user.tenant_id) ?? null;
  const activeLabel = activeTenant?.display_name ?? user?.tenant_id ?? "—";

  async function onPickTenant(id: string) {
    if (!user || id === user.tenant_id || switchTenant.isPending) return;
    try {
      await switchTenant.mutateAsync(id);
      // Land on the dashboard so the user sees the new tenant's data
      // without having to chase whichever stale-tenant detail page they
      // were on. Cheaper than per-page reconcile logic.
      nav("/", { replace: true });
    } catch {
      // mutation error surfaces in dev tools; nothing actionable for
      // the user here — the dropdown just stays on the old selection.
    }
  }

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-background/80 px-8 backdrop-blur">
      <div className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          placeholder="Search alerts, hosts, IPs…"
          className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex items-center gap-2">
        {canSwitchTenant && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label="Switch tenant"
                disabled={switchTenant.isPending}
                className="flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-60"
              >
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="max-w-[160px] truncate">{activeLabel}</span>
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={8}
                className="z-50 w-60 rounded-md border border-border bg-card p-1 text-sm shadow-md"
              >
                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                  Switch tenant
                </div>
                {user!.visible_tenants.map((t) => {
                  const isActive = t.id === user!.tenant_id;
                  return (
                    <DropdownMenu.Item
                      key={t.id}
                      onSelect={() => onPickTenant(t.id)}
                      className="flex cursor-pointer items-center justify-between rounded-sm px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
                    >
                      <span className="flex flex-col">
                        <span>{t.display_name}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {t.id}
                        </span>
                      </span>
                      {isActive && <Check className="h-4 w-4 text-primary" />}
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
        {onOpenChat && (
          <button
            type="button"
            onClick={onOpenChat}
            aria-label="Open Tyrael chat"
            title="Ask Tyrael"
            className="flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            <Sparkles className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Ask Tyrael</span>
          </button>
        )}
        <button
          type="button"
          aria-label="Notifications"
          className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <Bell className="h-4 w-4" />
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-red-500" />
        </button>

        <button
          type="button"
          onClick={toggle}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <div className="ml-2 hidden text-right text-xs text-muted-foreground md:block">
          <div className="font-medium text-foreground">
            {user?.full_name ?? user?.email ?? "—"}
          </div>
          {/* Show the tenant label here only when the chip dropdown
              isn't rendered (i.e. tenant-scoped users without a switch
              option). Platform admins see the chip and don't need this
              echoed underneath. */}
          {!canSwitchTenant && <div>Tenant: {activeLabel}</div>}
        </div>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-sm font-medium text-primary-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {user ? initials(user.full_name ?? user.email) : "?"}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={8}
              className="z-50 w-56 rounded-md border border-border bg-card p-1 text-sm shadow-md"
            >
              {user && (
                <div className="px-3 py-2">
                  <div className="font-medium">{user.full_name ?? user.email}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </div>
              )}
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={() => onLogout()}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
